<script setup lang="ts">
import { computed, ref, watch, watchEffect } from 'vue'
import * as R from 'remeda'
import { useQuery } from '@tanstack/vue-query'
import GwScrollCont from '@/components/lib/GwScrollCont/GwScrollCont.vue'
import GwText from '@/components/lib/base/GwText/GwText.vue'
import LoaderCircles from '@/components/Loaders/LoaderCircles.vue'
import type { FilterValue, TFilterField } from '../../../../types/api/reports'
import ItemBtnGroup from './ItemBtnGroup.vue'
import type { TFilterEmitValue } from './utils'
import GwInputNew from '../../../lib/GwInputNew.vue'
import {
  getUniquePaginatedValues,
  type FieldName,
  PAGE_LIMIT,
} from '../../../../store/api/gql/unique_values/queries'

interface Props {
  field: TFilterField
  isActive: boolean
  value: FilterValue
}

const MIN_SEARCH_LENGTH = 3

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:filterField': [TFilterEmitValue]
}>()

const searchValue = ref('')
const debouncedSearchValue = ref(searchValue.value)
const page = ref(0)
const options = ref([] as { id: string | number }[])
const hasNext = ref(true)
const hasScrolling = ref(false)

const searchQueryKey = computed(() => [
  'searchInput',
  props.field.id,
  debouncedSearchValue.value,
  page.value,
])

const searchValueDebouncer = R.debounce(
  (value: string) => {
    debouncedSearchValue.value = value
  },
  { waitMs: 300 },
)

watch(searchValue, (newSearchValue, currentSearchValue) => {
  if (
    newSearchValue.length < MIN_SEARCH_LENGTH &&
    currentSearchValue.length < newSearchValue.length
  ) {
    return
  }
  searchValueDebouncer.call(newSearchValue)
  page.value = 0
  options.value = []
  hasNext.value = true
})

const { data, isLoading } = useQuery({
  queryKey: searchQueryKey,
  queryFn: getUniquePaginatedValues(
    props.field.field_name as FieldName,
    debouncedSearchValue,
    page,
  ),
  throwOnError: true,
  enabled: hasNext,
})

watch(
  () => data.value?.unique_paginated_values.items,
  items => {
    if (!items) return
    if (items.length === 0) {
      hasNext.value = false
      return
    }
    options.value = [
      ...options.value,
      ...items.map(item => ({
        id: item[props.field.field_name],
      })),
    ]
  },
  { immediate: true },
)

watchEffect(() => {
  if (!hasScrolling.value && options.value.length > 0 && options.value.length % PAGE_LIMIT === 0) {
    page.value += 1
  }
})
</script>
<template>
  <div
    class="f-column mh-0 mw-0"
    style="gap: var(--app-gap-1)"
  >
    <GwInputNew
      :value="searchValue"
      @update:value="(newValue: string) => (searchValue = newValue)"
    />
    <GwScrollCont
      v-if="options.length"
      class="mh-0 mw-0"
      direction="vertical"
      hasOverflowSurfaces
      hasResizeObserver
      @scrolled:end="() => (page = page + 1)"
      @overflow="({ bottom }) => (hasScrolling = !!bottom)"
    >
      <ItemBtnGroup
        :field
        :isActive
        :options
        :value
        @update:filterField="emit('update:filterField', $event)"
      />
    </GwScrollCont>
    <GwText
      v-else-if="data"
      style="padding: var(--app-pad-2) var(--app-pad-3)"
      variant="text_grey_medium_500"
    >
      {{ $t('common.nothing_found') }}
    </GwText>
    <div
      v-if="isLoading"
      style="text-align: center"
      class="mt-1"
    >
      <LoaderCircles size="large" />
    </div>
  </div>
</template>
